// The Conflicts tab of a merge request (spec/dashboard.md section 12.3; spec/api-conventions.md section 12.2):
// `GET …/{mr}/conflicts`, one card per path with the three-way table — base | <target> (ours) | <source> (theirs), one row
// per conflicting field — and the choice per path (ours / theirs / custom: a JSON editor seeded with the resolved content,
// else theirs; "Delete the file" sends `content: null`); the recorded resolution pre-selects; Save resolutions posts
// `:resolve { resolutions }` for every path with a choice and the request re-reads.
import { useState } from 'react';
import { ops } from '../../api/operations';
import type { ConflictDetail, Json, MergeRequest, Parameter } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { JsonField } from '../../content/widgets/FieldWidget';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { formatValue } from '../../ui/DiffView';
import { Checkbox } from '../../ui/inputs';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { getAt } from '../../workspace/paths';
import { asApiError, mrKey, mrPrefix, plural } from './merge';
import { EmptyState } from '../../ui/Banner';
import { AlertCircle, CheckCircle2 } from '../../ui/icons';
import { LoadingPane } from '../../ui/PageStates';

type Choice = 'ours' | 'theirs' | 'custom';

interface Draft {
  choice: Choice | null;
  /** the custom content (a JSON value); `deleteFile` sends `content: null` instead */
  content: Json | undefined;
  deleteFile: boolean;
}

/** A synthetic `any` parameter for the JSON editor (as `jsonParam` of src/players/PlayerTree.tsx). */
function jsonParam(name: string): Parameter {
  return { id: name, unnyId: null, name, displayName: null, description: null, type: 'any', of: null, template: null, enum: null, group: null, assetKinds: [], regex: null, default: null, isUnique: false, isRequired: false, usedInName: false, flags: [], legacyFlags: 0, declaredBy: null } as unknown as Parameter;
}

function isChoice(v: string | null | undefined): v is Choice {
  return v === 'ours' || v === 'theirs' || v === 'custom';
}

function initialDraft(c: ConflictDetail): Draft {
  const recorded = isChoice(c.resolution) ? c.resolution : null;
  const resolved = c.resolved as Json | null | undefined;
  return {
    choice: recorded,
    content: (resolved ?? (c.theirs as Json | null) ?? undefined) as Json | undefined,
    deleteFile: recorded === 'custom' && (resolved === null || resolved === undefined),
  };
}

export function ConflictResolver({ mr }: { mr: MergeRequest }) {
  const { org, game, can } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const id = String(mr.number);
  const key = mrKey(org, game.slug, id, `conflicts@${mr.sourceSha}:${mr.targetSha}`);
  const conflictsQ = useQuery(key, () => client.data(ops.getMergeRequestConflicts, { path: { org, game: game.slug, mr: id } }) as unknown as Promise<ConflictDetail[]>);
  const conflicts = conflictsQ.data as ConflictDetail[] | undefined;
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState(false);
  const canWrite = can('content_write');

  const draftOf = (c: ConflictDetail): Draft => drafts[c.path] ?? initialDraft(c);
  const patch = (c: ConflictDetail, p: Partial<Draft>) => setDrafts((d) => ({ ...d, [c.path]: { ...(d[c.path] ?? initialDraft(c)), ...p } }));

  const save = async () => {
    if (!conflicts || saving) return;
    const resolutions = conflicts.flatMap((c): { path: string; choice: Choice; content?: Json | null }[] => {
      const d = draftOf(c);
      if (!d.choice) return [];
      if (d.choice !== 'custom') return [{ path: c.path, choice: d.choice }];
      return [{ path: c.path, choice: 'custom', content: d.deleteFile ? null : (d.content ?? null) }];
    });
    if (resolutions.length === 0) return;
    setSaving(true);
    try {
      await client.data(ops.resolveMergeRequestConflicts, { path: { org, game: game.slug, mr: id }, body: { resolutions } as never });
      toasts.success(`Saved ${plural(resolutions.length, 'resolution')}`);
      setDrafts({});
      queryCache.invalidate(mrPrefix(org, game.slug));
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setSaving(false);
    }
  };

  const error = conflictsQ.error ? asApiError(conflictsQ.error) : null;
  if (error) {
    return (
      <div className="banner banner-danger" role="alert" data-testid="mr-conflicts">
        <AlertCircle className="banner-icon" aria-hidden="true" />
        <div className="banner-body">
          {error.message}
          {error.requestId ? ` (request ${error.requestId})` : ''}
        </div>
        <Button size="s" onClick={() => queryCache.invalidate(key)}>
          Retry
        </Button>
      </div>
    );
  }
  if (!conflicts)
    return (
      <div data-testid="mr-conflicts">
        <LoadingPane rows={3} label="Loading the conflicts" />
      </div>
    );
  if (conflicts.length === 0) {
    return (
      <div data-testid="mr-conflicts">
        <EmptyState icon={<CheckCircle2 />} title="No conflicts" body={`The source merges cleanly into ${mr.targetBranch}.`} />
      </div>
    );
  }
  const anyChoice = conflicts.some((c) => draftOf(c).choice !== null);
  return (
    <div data-testid="mr-conflicts" className="mr-conflicts">
      {conflicts.map((c) => (
        <ConflictCard key={c.path} conflict={c} draft={draftOf(c)} onChange={(p) => patch(c, p)} sourceBranch={mr.sourceBranch} targetBranch={mr.targetBranch} disabled={!canWrite} />
      ))}
      <div className="mr-conflicts-actions">
        <Button variant="primary" onClick={() => void save()} busy={saving} disabled={!anyChoice} missingPermission={canWrite ? null : 'content_write'} title={anyChoice ? undefined : 'Choose a resolution first.'}>
          Save resolutions
        </Button>
      </div>
    </div>
  );
}

function ConflictCard({ conflict: c, draft, onChange, sourceBranch, targetBranch, disabled }: { conflict: ConflictDetail; draft: Draft; onChange: (p: Partial<Draft>) => void; sourceBranch: string; targetBranch: string; disabled: boolean }) {
  const group = `conflict-choice-${c.path}`;
  const pick = (value: 'ours' | 'theirs') => (draft.choice === value ? 'picked' : undefined);
  const fields = c.fields ?? [];
  return (
    <section className="pane mr-conflict" data-testid={`conflict-${c.path}`} aria-label={`Conflict ${c.path}`}>
      <header className="pane-head mr-conflict-head">
        {c.kind && <Badge tone="info">{c.kind}</Badge>}
        <strong>{c.name ?? c.path}</strong>
        {c.id && <code className="small muted">{c.id}</code>}
        <code className="small">{c.path}</code>
        <span className="spacer" />
        {c.resolution ? <Badge tone="success">resolved: {c.resolution}</Badge> : <Badge tone="warning">unresolved</Badge>}
      </header>
      <div className="mr-three-way-wrap">
      <table className="diff mr-three-way" data-testid={`conflict-table-${c.path}`}>
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">base</th>
            <th scope="col" className={pick('ours')}>{targetBranch} (ours)</th>
            <th scope="col" className={pick('theirs')}>{sourceBranch} (theirs)</th>
          </tr>
        </thead>
        <tbody>
          {fields.length === 0 ? (
            <tr>
              <th scope="row">(the whole file)</th>
              <td>{side(c.base, '')}</td>
              <td className={pick('ours')}>{side(c.ours, '')}</td>
              <td className={pick('theirs')}>{side(c.theirs, '')}</td>
            </tr>
          ) : (
            fields.map((f) => (
              <tr key={f}>
                <th scope="row">{f}</th>
                <td>{side(c.base, f)}</td>
                <td className={pick('ours')}>{side(c.ours, f)}</td>
                <td className={pick('theirs')}>{side(c.theirs, f)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
      <fieldset className="mr-choice" disabled={disabled}>
        <legend className="field-label">Resolution</legend>
        {(
          [
            ['ours', 'Keep ours', `Keep ${targetBranch}`],
            ['theirs', 'Take theirs', `Take ${sourceBranch}`],
            ['custom', 'Custom', 'Custom'],
          ] as const
        ).map(([value, aria, text]) => (
          <label key={value} className="check">
            <input type="radio" name={group} value={value} aria-label={aria} checked={draft.choice === value} onChange={() => onChange({ choice: value })} />
            <span>{text}</span>
          </label>
        ))}
      </fieldset>
      {draft.choice === 'custom' && (
        <div className="mr-custom">
          <Checkbox label="Delete the file" checked={draft.deleteFile} onChange={(e) => onChange({ deleteFile: e.target.checked })} disabled={disabled} />
          {!draft.deleteFile && <JsonField key={c.path} param={jsonParam(`Custom content of ${c.path}`)} value={draft.content} onCommit={(v) => onChange({ content: v })} disabled={disabled} />}
        </div>
      )}
    </section>
  );
}

/** The value at a dotted field path of one side (the whole file for ''); "(no file)" when the side has none. */
function side(file: unknown, path: string) {
  if (file === null || file === undefined) return <span className="muted">(no file)</span>;
  const v = path ? getAt(file, path) : file;
  return <code className="mr-value">{formatValue(v)}</code>;
}
